import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { useHistory } from 'react-router-dom';

import reducer from './reducer';
import { ProtectedRouteContext } from '../../../Context';

const initialState = {
    isFetching: true,
    headers: {},
    list: [],
    displayList: [],
    page: 0,
    rowsPerPage: 25,
    sortDir: 'Asc',
    sortKey: 'LastName',
    sort: 'LastName Asc',
    searchKeys: ['LastName', 'FirstName'],
    recOffset: 0,
    filterString: '',
    searchString: '',
    filterObjectString: '',
    selectedFilters: [],
    selectedRows: [],
};

const useRequestBuilder = (savedState, stateRef) => {
    const history = useHistory();
    const [state, dispatch] = React.useReducer(reducer, {
        ...initialState,
        ...savedState?.listState,
        ...stateRef?.current,
        isInitialized: false,
    });
    const { userSettings } = React.useContext(ProtectedRouteContext);

    React.useEffect(() => {
        stateRef.current = state;
    }, [state]);

    function search(entry) {
        return Object.keys(this).every((key) => {
            switch (key) {
                case 'InvoiceStatus':
                    return this[key].length === 0 || this[key].includes(entry[key].label);
                default:
                    return this[key].length === 0 || this[key].includes(entry[key]);
            }
        });
    }

    function sortGenerator(key, _state, _newState, _headerObj) {
        let sortFunc;
        switch (_headerObj?.type) {
            case 'date':
                sortFunc = (a, b) => {
                    let dateA = new Date(a[key]),
                        dateB = new Date(b[key]);
                    if (dateA?.getTime() < dateB?.getTime()) {
                        return _newState?.sortDir === 'Desc' ? 1 : -1;
                    } else if (dateB?.getTime() < dateA?.getTime()) {
                        return _newState?.sortDir === 'Desc' ? -1 : 1;
                    } else {
                        return 0;
                    }
                };
                break;
            case 'invoice-status':
                sortFunc = (a, b) => {
                    let statusA = a[key]?.id,
                        statusB = b[key]?.id;
                    if (statusA < statusB) {
                        return _newState?.sortDir === 'Desc' ? 1 : -1;
                    } else if (statusB < statusA) {
                        return _newState?.sortDir === 'Desc' ? -1 : 1;
                    } else {
                        return 0;
                    }
                };
                break;
            default:
                sortFunc = (a, b) => {
                    if (a[key] < b[key]) {
                        return _newState?.sortDir === 'Desc' ? 1 : -1;
                    } else if (b[key] < a[key]) {
                        return _newState?.sortDir === 'Desc' ? -1 : 1;
                    } else {
                        return 0;
                    }
                };
        }

        return sortFunc;
    };

    var transformInvoiceStatus = (statuses) => {
        return statuses
            .map((status) => {
                return {
                    id: status.StatusID,
                    label: status.Description,
                    hex: status.Color,
                };
            })
            .reduce((prev, cur, i) => {
                return { ...prev, [cur?.label]: cur };
            }, {});
    };

    var mapInvoice = (invoice, _statuses) => {
        let _status = _statuses ? _statuses[invoice?.InvoiceStatusID] : state.statuses[invoice?.InvoiceStatusID];
        let _actionLabel;

        switch (invoice?.InvoiceStatusID) {
            case 1:
                _actionLabel = 'Create';
                break;
            case 2:
                _actionLabel = 'Edit';
                break;
            case 3:
            case 4:
                _actionLabel = userSettings?.UserType === 2 ? 'View' : 'Edit';
                break;
            case 5:
            case 6:
                _actionLabel = 'View';
                break;
            case 8:
                _actionLabel = userSettings?.UserType === 2 ? 'View' : 'Edit';
                break;
            default:
                _actionLabel = '';
        }

        return {
            ...invoice,
            href: `/invoice-history/${invoice.UniqueID}/${invoice.AppTermID}`,
            InvoiceStatus: {
                ..._status,
                actionLabel: _actionLabel,
            },
        };
    };

    const handleNav = async (item) => {
        dispatch({ type: 'fetching' });

        if (typeof item === 'string') {
            try {
                return history.push({
                    pathname: item,
                    state: {
                        listState: {
                            ...state,
                            list: [],
                            currentCount: 0,
                            recOffset: 0,
                        },
                        backPathName: '/finances',
                    },
                });
            } catch (e) {
                toast.error('API error.');
                dispatch({ type: 'fetched' });
            }
        }

        switch (item.InvoiceStatusID) {
            case 1:
                let id = toast.loading('Creating invoice...');
                try {
                    let { newInvoice } = await API.patch('ShipyardAPI', `/finances/invoices/${item.AppInstID}`, {
                        body: {
                            pPeriodID: item.AppTermSchoolPeriodID,
                            pInvoiceID: item.InvoiceID,
                        },
                    });

                    toast.success(`Invoice #${newInvoice.InvoiceNumber} created!`);

                    history.push({
                        pathname: `/finances/invoices/${newInvoice.AppInstInvoiceID}`,
                        state: {
                            invoice: item,
                            statusRef: state.statusRef,
                            listState: {
                                ...state,
                                list: [],
                                currentCount: 0,
                                recOffset: 0,
                            },
                        },
                    });
                } catch (e) {
                    toast.error('API Failure.');
                    console.error(e);
                    dispatch({ type: 'fetched' });
                } finally {
                    toast.dismiss(id);
                }
                break;
            default:
                history.push({
                    pathname: `/finances/invoices/${item.InvoiceID}`,
                    state: {
                        invoice: item,
                        statusRef: state.statusRef,
                        listState: {
                            ...state,
                            list: [],
                            currentCount: 0,
                            recOffset: 0,
                        },
                    },
                });
        }
    };

    const retrieveList = async (term, filterString, sort, recOffset) => {
        dispatch({ type: 'fetching' });

        let requestBody = {
            pFilterString: filterString !== undefined ? filterString : state.filterString,
            pSort: sort !== undefined ? sort : state.sort,
            pRecOffset: recOffset !== undefined ? recOffset : state.recOffset,
            pAppTermID: term,
        };

        try {
            let [{ invoiceHeaders, invoicesList }, filters] = await Promise.all([
                API.post('ShipyardAPI', '/finances/invoices', {
                    body: requestBody,
                }),
                API.get('ShipyardAPI', '/finances/invoices/filters'),
            ]);

            if (!state?.isInitialized) {
                var _headers = invoiceHeaders.map((header) => {
                    return {
                        ...header,
                        label: t(header.label),
                    };
                });

                var _filters = {};
                for (const [key, value] of Object.entries(filters)) {
                    let parsedFilters = {};
                    switch (key) {
                        case 'InvoiceStatus':
                            parsedFilters = value.map((val) => {
                                return { id: t(val.Label), label: t(val.Label) };
                            });
                            break;
                        case 'Grade':
                            parsedFilters = value.map((val) => {
                                return { id: val.Description, label: val.Description };
                            });
                            break;
                        case 'Period':
                            parsedFilters = value.map((val) => {
                                return { id: val.PeriodDescription, label: val.PeriodDescription };
                            });
                            break;
                        default:
                            parsedFilters = value.map((val) => {
                                return { id: val, label: val };
                            });
                    }

                    _filters[key] = {
                        label: t(`Filters.${key}`),
                        filters: parsedFilters,
                    };
                }

                var _statuses = {};
                filters.InvoiceStatus.forEach((status) => {
                    _statuses[status.StatusID] = {
                        label: status.Description,
                        hex: status.Color,
                        id: status.StatusID,
                    };
                });

                var _statusRef = transformInvoiceStatus(filters.InvoiceStatus);
            }

            let _list = invoicesList.map((invoice) => mapInvoice(invoice, _statuses));

            let newList = requestBody.pRecOffset === 0 ? _list : [...state.list, ..._list];

            let newState = {
                list: newList,
                isInitialized: true,
                totalRecordCount: Number.isInteger(_list?.[0]?.TotalRecordCount) ? _list[0].TotalRecordCount : 0,
                hasMore: newList.length < _list?.[0]?.TotalRecordCount,
                recOffset: requestBody.pRecOffset + 9999,
            };

            if (requestBody.pRecOffset === 0 && state.selectedRows.length > 0) newState.selectedRows = [];

            if (_filters && _statuses && _headers && _statusRef) {
                newState.filters = _filters;
                newState.statuses = _statuses;
                newState.headers = _headers;
                newState.statusRef = _statusRef;
            }

            let _savedfilters = state.selectedFilters;
            let displayList = [...newList].filter(search, _savedfilters);
            displayList = displayList.filter((val) => {
                for (let key in state.searchKeys) {
                    let compVal = val[state.searchKeys[key]].toUpperCase();
                    if (compVal.includes(state.searchValue?.toUpperCase()  || '')) return true;
                }
                return false;
            });
            let parsedList = [...displayList];
            let findHeader = (header) => header?.id === state.sortKey;
            let headerObj = _headers ? _headers?.find(findHeader) : state?.headers?.find(findHeader);
            let sortFunc = sortGenerator(state.sortKey, state, state, headerObj);

            newState.displayList = [...(parsedList.sort(sortFunc))];
            newState.currentCount = displayList.length;

            if (requestBody.pFilterString === '') {
                newState.totalUnfilteredCount = _list[0]?.TotalRecordCount || 0;
            }

            dispatch({ type: 'update', newState });
        } catch (e) {
            toast.error('API failure.');
        } finally {
            dispatch({ type: 'fetched' });
        }
    };

    const handleFilterString = (newVal, type) => {
        let displayList,
            _filters,
            selectedFilterEntries,
            newState = {
                page: 0,
            };

        switch (type) {
            case 'search':
                _filters = state.selectedFilters;
                displayList = [...state.list].filter(search, _filters);
                displayList = displayList.filter((val) => {
                    for (let key in state.searchKeys) {
                        let compVal = val[state.searchKeys[key]].toUpperCase();
                        if (compVal.includes(newVal.toUpperCase())) return true;
                    }
                    return false;
                });

                newState.searchValue = newVal;
                newState.displayList = displayList;
                newState.currentCount = displayList.length;
                break;
            case 'filter':
                let oldFilters = { ...state?.selectedFilters };
                for(let key in newVal) {
                    delete oldFilters[key];
                };

                selectedFilterEntries = [...Object.entries(newVal), ...Object.entries(oldFilters)];

                _filters = Object.fromEntries(selectedFilterEntries);
                displayList = [...state.list].filter(search, _filters);
                displayList = displayList.filter((val) => {
                    for (let key in state.searchKeys) {
                        let compVal = val[state.searchKeys[key]].toUpperCase();
                        if (compVal.includes(state.searchValue?.toUpperCase()  || '')) return true;
                    }
                    return false;
                });

                newState.displayList = displayList;
                newState.currentCount = displayList.length;
                newState.selectedFilters = _filters;
                break;
            default:
                return;
        }

        let sortedList = [...newState.displayList];
        let headerObj = state?.headers?.find((header) => header?.id === state.sortKey);
        let sortFunc = sortGenerator(state.sortKey, state, state, headerObj);
        newState.displayList = [...sortedList.sort(sortFunc)];
        newState.selectedRows = [];

        dispatch({
            type: 'update',
            newState,
        });
    };

    const handleSort = (key) => {
        let newState = {};

        if (key !== state.sortKey) {
            newState.sortKey = key;
            newState.sortDir = 'Asc';

            newState.sort = `${key} Asc`;
        } else {
            newState.sortDir = state?.sortDir === 'Asc' ? 'Desc' : 'Asc';

            newState.sort = `${key} ${newState.sortDir}`;
        }

        let parsedList = [...state?.displayList] || [];
        let headerObj = state?.headers?.find((header) => header?.id === key);
        let sortFunc = sortGenerator(key, state, newState, headerObj);

        newState.displayList = parsedList.sort(sortFunc);

        dispatch({ type: 'update', newState });
    };

    const loadMore = async (term) => {
        await retrieveList(term);
    };

    const handleRowSelection = async (newRow, event) => {
        switch (newRow) {
            case 'all':
                dispatch({
                    type: 'update',
                    newState: {
                        ...state,
                        selectedRows: event.target.checked ? state.displayList : [],
                    },
                });
                break;
            default:
                let _selectedRows = new Set(state.selectedRows);
                if (event.target.checked) _selectedRows.add(newRow);
                else _selectedRows.delete(newRow);

                dispatch({
                    type: 'update',
                    newState: {
                        ...state,
                        selectedRows: Array.from(_selectedRows),
                    },
                });
        }
    };

    const refreshList = async (term) => {
        dispatch({ type: 'refresh' });
        dispatch({ type: 'fetching' });

        try {
            let newState = {};
            const { invoicesList } = await API.post('ShipyardAPI', '/finances/invoices', {
                body: {
                    pAppTermID: term,
                    pFilterString: state.filterString,
                    pSort: state.sort,
                    pRecOffset: 0,
                },
            });

            let _list = invoicesList.map((invoice) => mapInvoice(invoice));

            let _savedfilters = state.selectedFilters;
            let displayList = [..._list].filter(search, _savedfilters);
            displayList = displayList.filter((val) => {
                for (let key in state.searchKeys) {
                    let compVal = val[state.searchKeys[key]].toUpperCase();
                    if (compVal.includes(state.searchValue?.toUpperCase()  || '')) return true;
                }
                return false;
            });
            let parsedList = [...displayList];
            let headerObj = state?.headers?.find((header) => header?.id === state.sortKey);
            let sortFunc = sortGenerator(state.sortKey, state, state, headerObj);

            newState.displayList = [...(parsedList.sort(sortFunc))];
            newState.currentCount = displayList.length;

            if (state.filterString === '') {
                newState.totalUnfilteredCount = _list[0]?.TotalRecordCount || 0;
            }

            dispatch({
                type: 'update',
                newState: {
                    list: _list,
                    recOffset: 9999,
                    currentCount: invoicesList.length,
                    totalRecordCount: Number.isInteger(invoicesList?.[0]?.TotalRecordCount) ? invoicesList[0].TotalRecordCount : 0,
                    hasMore: invoicesList.length < invoicesList?.[0]?.TotalRecordCount,
                    ...newState,
                },
            });
        } catch (e) {
            toast.error('API error.');
        } finally {
            dispatch({ type: 'fetched' });
        }
    };

    const handlePageChange = (event, newPage) => {
        dispatch({
            type: 'update',
            newState: {
                page: newPage,
            },
        });
    };

    const handleRowsPerPageChange = (event) => {
        dispatch({
            type: 'update',
            newState: {
                page: 0,
                rowsPerPage: event.target.value,
            },
        });
    };

    return {
        ...state,
        retrieveList,
        handleFilterString,
        handleSort,
        handleNav,
        loadMore,
        handleRowSelection,
        refreshList,
        handlePageChange,
        handleRowsPerPageChange,
    };
};

export default useRequestBuilder;
